import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import agentApiService from '../../services/agentApiService';

const SupportTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetails();
    }
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      console.log('🔍 [SupportTicketDetail] Fetching ticket details for ID:', ticketId);
      
      const response = await agentApiService.getSupportTicketDetails(ticketId);
      console.log('📊 [SupportTicketDetail] API Response:', response);
      
      if (response.success) {
        setTicket(response.ticket);
        console.log('✅ [SupportTicketDetail] Ticket details loaded successfully');
      } else {
        console.error('❌ [SupportTicketDetail] API returned error:', response.message);
        setError(response.message || 'Failed to fetch ticket details');
        toast.error(response.message || 'Failed to fetch ticket details');
      }
    } catch (error) {
      console.error('❌ [SupportTicketDetail] Network/API error:', error);
      setError(error.message);
      toast.error('Failed to fetch ticket details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      setAddingComment(true);
      console.log('💬 [SupportTicketDetail] Adding comment to ticket:', ticketId);
      
      const response = await agentApiService.addCommentToTicket(ticketId, { comment: newComment });
      console.log('✅ [SupportTicketDetail] Comment added:', response);
      
      if (response.success) {
        toast.success('Comment added successfully!');
        setShowCommentModal(false);
        setNewComment('');
        setTicket(response.ticket); // Update ticket with new comment
      } else {
        toast.error(response.message || 'Failed to add comment');
      }
    } catch (error) {
      console.error('❌ [SupportTicketDetail] Error adding comment:', error);
      toast.error('Failed to add comment: ' + error.message);
    } finally {
      setAddingComment(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'assigned':
        return 'bg-purple-100 text-purple-800';
      case 'inprogress':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolve':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'fas fa-ticket-alt text-blue-600';
      case 'assigned':
        return 'fas fa-user-check text-purple-600';
      case 'inprogress':
        return 'fas fa-spinner text-yellow-600';
      case 'resolve':
        return 'fas fa-check-circle text-green-600';
      default:
        return 'fas fa-question-circle text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error Loading Ticket Details</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/agent/dashboard/supportticket')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Back to Tickets
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 text-xl mb-4">Ticket not found</div>
          <button
            onClick={() => navigate('/agent/dashboard/supportticket')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Back to Tickets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/agent/dashboard/supportticket')}
              className="text-gray-600 hover:text-gray-800"
            >
              <i className="fas fa-arrow-left text-xl"></i>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Support Ticket Details</h1>
              <p className="text-gray-600">Ticket ID: {ticket.ticketId}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <i className={getStatusIcon(ticket.ticketStatus)}></i>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeClass(ticket.ticketStatus)}`}>
              {ticket.ticketStatus}
            </span>
          </div>
        </div>
      </div>

      {/* Ticket Information Card */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Ticket Information</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                {ticket.agentName || 'N/A'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ticket ID</label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                {ticket.ticketId || 'N/A'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                {formatDate(ticket.createdDate)}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Query Type</label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  {ticket.queryType || 'N/A'}
                </span>
              </div>
            </div>
            {ticket.assignedToName && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                  {ticket.assignedToName}
                </div>
              </div>
            )}
            {ticket.bookingId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Booking ID</label>
                <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                  {ticket.bookingId}
                </div>
              </div>
            )}
            {ticket.cancellationId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation ID</label>
                <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                  {ticket.cancellationId}
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Agent Query</label>
            <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-lg">
              {ticket.agentQuery || 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* Add Comment Section */}
      {ticket.ticketStatus?.toLowerCase() !== 'resolve' && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Add Comment</h2>
          </div>
          <div className="p-6">
            <button
              onClick={() => setShowCommentModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-300"
            >
              <i className="fas fa-comment mr-2"></i>
              Add Comment
            </button>
          </div>
        </div>
      )}

      {/* Comments Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Comments ({ticket.comments?.length || 0})
          </h2>
        </div>
        <div className="p-6">
          {ticket.comments && ticket.comments.length > 0 ? (
            <div className="space-y-4">
              {ticket.comments.map((comment, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="fas fa-user text-blue-600 text-sm"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {comment.supportName || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(comment.cratedDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-700 ml-10">
                    {comment.comment || 'No comment text'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-4">
                <i className="fas fa-comments"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Comments Yet</h3>
              <p className="text-gray-500">Be the first to add a comment to this ticket.</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Comment Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Add Comment</h3>
              <button
                onClick={() => setShowCommentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleAddComment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment *
                </label>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your comment here..."
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCommentModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded-lg transition duration-300"
                  disabled={addingComment}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingComment}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingComment ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Adding...
                    </>
                  ) : (
                    'Add Comment'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicketDetail;